import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { apiRequest, configureApi, requestCoordinator } from "@/api/client";
import type {
  AuthPayload,
  Organization,
  SingleResponse,
  User,
} from "@/types/domain";
import { idempotency } from "@/utils/idempotency";

const ORG_STORAGE_KEY = "etax.current-org-id";

export const useSessionStore = defineStore("session", () => {
  const user = ref<User | null>(null);
  const organizations = ref<Organization[]>([]);
  const currentOrgId = ref(localStorage.getItem(ORG_STORAGE_KEY) ?? "");
  const csrfToken = ref("");
  const initialized = ref(false);

  const currentOrganization = computed(
    () =>
      organizations.value.find((org) => org.id === currentOrgId.value) ?? null,
  );
  const permissions = computed(
    () => new Set(currentOrganization.value?.permissions ?? []),
  );
  const isAuthenticated = computed(() => user.value !== null);

  function syncApi(): void {
    configureApi({
      csrfToken: csrfToken.value,
      organizationId: currentOrgId.value,
      onSessionExpired: clear,
    });
  }

  function apply(payload: AuthPayload): void {
    user.value = payload.user;
    organizations.value = payload.organizations;
    csrfToken.value = payload.csrfToken;
    if (!organizations.value.some((org) => org.id === currentOrgId.value)) {
      currentOrgId.value = organizations.value[0]?.id ?? "";
    }
    if (currentOrgId.value)
      localStorage.setItem(ORG_STORAGE_KEY, currentOrgId.value);
    syncApi();
  }

  async function refreshCurrentOrganization(): Promise<void> {
    if (!currentOrgId.value) return;
    try {
      const response = await apiRequest<SingleResponse<Organization>>(
        "/organizations/current",
      );
      const index = organizations.value.findIndex(
        (organization) => organization.id === currentOrgId.value,
      );
      if (index >= 0)
        organizations.value[index] = {
          ...organizations.value[index],
          ...response.data,
        };
    } catch {
      // Authentication remains usable even if organization metadata refresh fails.
    }
  }

  async function fetchCsrf(): Promise<string> {
    const response =
      await apiRequest<SingleResponse<{ csrfToken: string }>>("/auth/csrf");
    csrfToken.value = response.data.csrfToken;
    syncApi();
    return csrfToken.value;
  }

  async function login(loginName: string, password: string): Promise<void> {
    await fetchCsrf();
    const response = await apiRequest<SingleResponse<AuthPayload>>(
      "/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ loginName, password }),
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken.value,
        },
      },
    );
    apply(response.data);
    await refreshCurrentOrganization();
  }

  async function restore(): Promise<boolean> {
    try {
      const response =
        await apiRequest<SingleResponse<AuthPayload>>("/auth/me");
      apply(response.data);
      await refreshCurrentOrganization();
      return true;
    } catch {
      clear();
      return false;
    } finally {
      initialized.value = true;
    }
  }

  async function logout(): Promise<void> {
    try {
      if (!csrfToken.value) await fetchCsrf();
      await apiRequest("/auth/logout", {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken.value },
      });
    } finally {
      clear();
    }
  }

  function switchOrganization(id: string): void {
    if (
      id === currentOrgId.value ||
      !organizations.value.some((org) => org.id === id)
    )
      return;
    requestCoordinator.cancelAll();
    idempotency.clearOrganization(currentOrgId.value);
    currentOrgId.value = id;
    localStorage.setItem(ORG_STORAGE_KEY, id);
    syncApi();
  }

  function can(permission?: string): boolean {
    return (
      !permission ||
      permissions.value.has(permission) ||
      currentOrganization.value?.role === "ADMIN"
    );
  }

  function canAny(...requested: string[]): boolean {
    return requested.some((permission) => can(permission));
  }

  function clear(): void {
    requestCoordinator.cancelAll();
    idempotency.clearAll();
    user.value = null;
    organizations.value = [];
    csrfToken.value = "";
    configureApi({ csrfToken: "", organizationId: "" });
  }

  syncApi();
  return {
    user,
    organizations,
    currentOrgId,
    csrfToken,
    initialized,
    currentOrganization,
    permissions,
    isAuthenticated,
    login,
    logout,
    restore,
    refreshCurrentOrganization,
    switchOrganization,
    can,
    canAny,
    clear,
  };
});
